import {useState} from 'react';
import {Image, Link} from '@shopify/hydrogen';
import {AnimatePresence, motion} from 'framer-motion';
import clsx from 'clsx';

import type {SanityMenuLink} from '../../types';

import {useScrollFreeze} from '../../hooks/useScrollFreeze';
import MenuIcon from '../icons/Menu';
import React from 'react';

type Props = {
  menuLinks: SanityMenuLink[];
  flip: boolean;
  toggleMobileNav: () => void;
  mobileNavOpen: boolean;
  countries: any;
};

const LinkSwitch = ({external, externalLink, link, children, ...rest}: any) => {
  switch (external) {
    case true:
      return (
        <a
          aria-label="a link taking you to the page displayed by the label"
          href={externalLink?.url}
          target={externalLink?.newWindow ? '_blank' : '_self'}
          rel="noreferrer"
          {...rest}
        >
          {children}
        </a>
      );

    default:
      return (
        <Link
          aria-label="a link taking you to the page displayed by the label"
          to={link?.slug || '/'}
          {...rest}
        >
          {children}
        </Link>
      );
  }
};

const MenuItem = ({
  image,
  link,
  onClick,
  external,
  externalLink,
  ...rest
}: any) => {
  return (
    <LinkSwitch
      external={external}
      externalLink={externalLink}
      link={link}
      onClick={onClick}
      className={clsx([
        'transition-standard flex items-center gap-x-4 overflow-hidden border-t border-light-grey py-2',
      ])}
      {...rest}
    >
      <div
        className={clsx([
          'transition-standard relative h-auto w-auto',
          'w-[90px]',
        ])}
      >
        <Image
          data={image?.asset}
          alt={link?.title || externalLink?.title}
          height="inherit"
          width="inherit"
          className="transition-standard block"
        />
      </div>
      <div className="transition-standard h-full w-full">
        <h5 className={clsx(['transition-standard richH5 block font-bold'])}>
          {link?.title || externalLink?.title}
        </h5>
      </div>
    </LinkSwitch>
  );
};

const Accordion = ({
  i,
  expanded,
  setExpanded,
  title,
  items,
  bottomLink,
  toggleMobileNav,
}: any) => {
  const isOpen = i === expanded;
  return (
    <>
      <motion.div
        className={`text-teal flex cursor-pointer flex-row items-center justify-between gap-x-12`}
        initial={false}
        onClick={() => setExpanded(isOpen ? false : i)}
      >
        <h4 className="blockH4 py-5 font-bold">{title}</h4>
        <span
          className={clsx([
            'blockH6 transition-standard',
            isOpen ? 'rotate-45 text-orange' : 'rotate-0',
          ])}
        >
          +
        </span>
      </motion.div>
      <AnimatePresence initial={false}>
        <motion.section
          key="content"
          initial={{height: 0}}
          animate={isOpen ? {height: 'fit-content'} : {height: 0}}
          transition={{ease: 'easeInOut'}}
        >
          <motion.div
            className={`${!isOpen && 'pointer-events-none'} pb-5`}
            initial={{opacity: 0}}
            animate={isOpen ? {opacity: 1} : {opacity: 0}}
            transition={{ease: 'easeInOut'}}
          >
            {items?.map((item: any) => (
              <MenuItem key={item._key} {...item} onClick={toggleMobileNav} />
            ))}
            {bottomLink?.slug && (
              <div className="border-t border-light-grey pt-3">
                <Link
                  onClick={toggleMobileNav}
                  className="linkTextNavigation richH5 font-bold text-orange"
                  to={bottomLink?.slug}
                >
                  {bottomLink?.title}
                </Link>
              </div>
            )}
          </motion.div>
        </motion.section>
      </AnimatePresence>
    </>
  );
};

const RenderLinks = ({link, toggleMobileNav, i}: any) => {
  const [expanded, setExpanded] = useState(false);

  switch (link._type) {
    case 'linkExternal':
      return (
        <div className="flex items-center py-5" key={link._key}>
          <a
            onClick={toggleMobileNav}
            className="linkTextNavigation"
            href={link.url}
            rel="noreferrer"
            target={link.newWindow ? '_blank' : '_self'}
          >
            {link.title}
          </a>
        </div>
      );
    case 'linkInternal':
      if (!link.slug) {
        return null;
      }

      return (
        <div className="flex items-center py-5" key={link._key}>
          <Link
            onClick={toggleMobileNav}
            className="linkTextNavigation"
            to={link.slug}
          >
            {link.title}
          </Link>
        </div>
      );
    case 'sideMenu':
      return (
        <Accordion
          key={i}
          i={i}
          expanded={expanded}
          setExpanded={setExpanded}
          {...link}
          toggleMobileNav={toggleMobileNav}
        />
      );
    default:
      return <></>;
  }
};

const ModalInner = ({menuLinks, toggleMobileNav}: any) => {
  useScrollFreeze();
  return (
    <motion.div
      initial={{opacity: 0}}
      animate={{opacity: 1}}
      exit={{opacity: 0}}
      className="fixed left-0 top-0 h-screen w-full overflow-y-scroll bg-white"
    >
      <div className="blockH4 mt-36 flex h-full flex-col justify-start  px-gutter font-bold">
        {menuLinks?.map((link: any) => (
          <React.Fragment key={link._key}>
            <RenderLinks
              key={link._key}
              link={link}
              toggleMobileNav={toggleMobileNav}
            />
            <div className="w-full border-t border-light-grey" />
          </React.Fragment>
        ))}
        <RenderLinks
          link={{
            _type: 'linkInternal',
            _key: 'linkInternal',
            title: 'Retailers',
            slug: '/stockists',
          }}
          toggleMobileNav={toggleMobileNav}
        />
        <div className="w-full border-t border-light-grey" />
      </div>
    </motion.div>
  );
};

export default function MobileNavigation({
  menuLinks,
  flip,
  toggleMobileNav,
  mobileNavOpen,
  countries,
}: Props) {
  return (
    <>
      <button
        className={clsx(
          'relative z-30 mt-1 flex items-center font-bold text-sm duration-200',
          'xl:hidden',
        )}
        aria-label="Open Navigation"
        onClick={toggleMobileNav}
      >
        <MenuIcon flip={flip} isOpen={mobileNavOpen} />
      </button>

      <AnimatePresence mode="wait">
        {mobileNavOpen && (
          <ModalInner
            menuLinks={menuLinks}
            toggleMobileNav={toggleMobileNav}
            countries={countries}
          />
        )}
      </AnimatePresence>
    </>
  );
}
